import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ArrowLeft, Check, Minus, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart, useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const { addToCart } = useCart();
  const { user, login } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  const categoryRouteMap = {
    magliette: { to: "/magliette", label: "MAGLIETTE" },
    felpe: { to: "/felpe", label: "FELPE" },
    homemade: { to: "/abbigliamento", label: "ABBIGLIAMENTO" },
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
        if (response.data.sizes?.length > 0) {
          setSelectedSize(response.data.sizes[0]);
        }
        if (response.data.colors?.length > 0) {
          setSelectedColor(response.data.colors[0]);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleAddToCart = async () => {
    if (!user) {
      login();
      return;
    }

    if (!selectedSize || !selectedColor) {
      toast.error("Seleziona taglia e colore");
      return;
    }

    setAdding(true);
    const success = await addToCart(productId, selectedSize, selectedColor, quantity);
    setAdding(false);

    if (success) {
      toast.success("Aggiunto al carrello!", {
        description: `${product.name} - ${selectedSize} - ${selectedColor}`,
      });
    } else {
      toast.error("Errore durante l'aggiunta al carrello");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-black border-t-accent-pink animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl mb-4">PRODOTTO NON TROVATO</h1>
          <Link to="/" className="btn-brutal">TORNA ALLA HOME</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="border-b-2 border-black py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {(() => {
            const backTarget = categoryRouteMap[product.category] || {
              to: "/abbigliamento",
              label: "ABBIGLIAMENTO",
            };
            return (
              <Link 
                to={backTarget.to}
                className="inline-flex items-center font-mono text-sm hover:text-accent-pink"
              >
                <ArrowLeft size={16} className="mr-2" />
                TORNA A {backTarget.label}
              </Link>
            );
          })()}
        </div>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="sticky top-32"
          >
            <div className="border-4 border-black shadow-brutal-lg overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full aspect-square object-cover"
              />
            </div>

            {/* Collection Badge */}
            <div className="mt-4">
              <span className={`
                inline-block font-mono text-sm uppercase px-4 py-2 border-2 border-black
                ${product.collection === 'astro' ? 'bg-accent-blue' : ''}
                ${product.collection === 'drinks' ? 'bg-accent-pink text-white' : ''}
                ${product.collection === 'quotes' ? 'bg-accent-yellow' : ''}
                ${product.collection === 'handmade' ? 'bg-accent-green' : ''}
              `}>
                COLLEZIONE {product.collection}
              </span>
            </div>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h1 className="font-heading text-5xl sm:text-6xl mb-4" data-testid="product-name">
              {product.name}
            </h1>

            <p className="font-mono text-3xl font-bold mb-6" data-testid="product-price">
              €{product.price.toFixed(2)}
            </p>

            <p className="font-body text-lg text-gray-700 mb-8">
              {product.description}
            </p>

            {/* Size Selector */}
            <div className="mb-6">
              <label className="font-heading text-xl mb-3 block">TAGLIA</label>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`
                      w-14 h-14 border-2 border-black font-mono font-bold
                      transition-all duration-200
                      ${selectedSize === size 
                        ? 'bg-black text-white shadow-brutal' 
                        : 'bg-white hover:bg-gray-100'
                      }
                    `}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selector */}
            <div className="mb-6">
              <label className="font-heading text-xl mb-3 block">COLORE</label>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`
                      px-6 py-3 border-2 border-black font-body font-semibold
                      transition-all duration-200
                      ${selectedColor === color 
                        ? 'bg-black text-white shadow-brutal' 
                        : 'bg-white hover:bg-gray-100'
                      }
                    `}
                    data-testid={`color-${color}`}
                  >
                    {selectedColor === color && <Check size={16} className="inline mr-2" />}
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <label className="font-heading text-xl mb-3 block">QUANTITÀ</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 border-2 border-black flex items-center justify-center hover:bg-gray-100"
                  data-testid="quantity-decrease"
                >
                  <Minus size={20} />
                </button>
                <span className="font-mono text-2xl font-bold w-12 text-center" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-12 border-2 border-black flex items-center justify-center hover:bg-gray-100"
                  data-testid="quantity-increase"
                >
                  <Plus size={20} />
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={adding}
              className={`
                w-full py-5 font-heading text-2xl border-4 border-black
                flex items-center justify-center gap-3
                transition-all duration-200
                ${adding 
                  ? 'bg-gray-200 cursor-not-allowed' 
                  : 'bg-accent-pink text-white hover:shadow-brutal-lg hover:-translate-y-1'
                }
              `}
              data-testid="add-to-cart-btn"
            >
              {adding ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent animate-spin"></div>
              ) : (
                <>
                  <ShoppingCart size={28} />
                  AGGIUNGI AL CARRELLO
                </>
              )}
            </button>

            {/* Info */}
            <div className="mt-8 space-y-4 border-t-2 border-black pt-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent-yellow border-2 border-black flex items-center justify-center flex-shrink-0">
                  <Check size={24} />
                </div>
                <div>
                  <h4 className="font-heading text-lg">RICAMO FATTO A MANO</h4>
                  <p className="font-body text-sm text-gray-600">
                    Ogni pezzo è unico e curato nei minimi dettagli
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent-blue border-2 border-black flex items-center justify-center flex-shrink-0">
                  <Check size={24} />
                </div>
                <div>
                  <h4 className="font-heading text-lg">SPEDIZIONE GRATUITA</h4>
                  <p className="font-body text-sm text-gray-600">
                    Su tutti gli ordini sopra i €50
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
